import type { FileObjectType } from "@jsq/ds/core";
import {
  DataType,
  DATA_TYPE_ORDER_PRIORITY,
  TOOL_TYPE_MAPPING,
  FILE_PARAMETER_MAPPING,
  TOOL_ID_MAPPING,
} from "../OpcoImport/constants";

export interface BuildOpcoImportJobConfigParams {
  filesWithTypes: Array<{ file: FileObjectType; dataType?: string }>;
  uploadResults: Array<{ objectKey: string; file: File }>;
  arenaUserEmail: string;
  arenaDomain: string;
}

export interface JobTool {
  toolId: string;
  toolType: string;
  options: Array<{ key: string; value: string }>;
}

interface JobToolWithDataType extends JobTool {
  dataType: string;
}

export interface JobConfig {
  tools: JobTool[];
}

function getToolTypeForDataType(dataType: string): string {
  if (dataType in TOOL_TYPE_MAPPING) {
    return TOOL_TYPE_MAPPING[dataType as DataType];
  }
  throw new Error(`Unknown data type: ${dataType}`);
}

function getFileParameterKey(dataType: string): string {
  if (dataType in FILE_PARAMETER_MAPPING) {
    return FILE_PARAMETER_MAPPING[dataType as DataType];
  }
  throw new Error(`Unknown data type: ${dataType}`);
}

function getToolId(dataType: string): string {
  if (dataType in TOOL_ID_MAPPING) {
    return TOOL_ID_MAPPING[dataType as DataType];
  }
  throw new Error(`Unknown data type: ${dataType}`);
}

export function buildOpcoImportJobConfig({
  filesWithTypes,
  uploadResults,
  arenaUserEmail,
  arenaDomain,
}: BuildOpcoImportJobConfigParams): JobConfig {
  const toolsWithDataType: JobToolWithDataType[] = [];

  for (const { file, dataType } of filesWithTypes) {
    if (!dataType) continue;

    const uploadResult = uploadResults.find((result) => result.file.name === file.file.name);

    if (!uploadResult) {
      throw new Error(`Upload result not found for file: ${file.file.name}`);
    }

    const toolType = getToolTypeForDataType(dataType);
    const fileParameterKey = getFileParameterKey(dataType);
    const toolId = getToolId(dataType);

    const options = [
      { key: "arena", value: arenaDomain },
      { key: "user", value: arenaUserEmail },
      { key: fileParameterKey, value: `${arenaDomain}/${file.file.name}` },
    ];

    // Add single_positions: true for load_positions_transactions tools
    if (toolType === "load_positions_transactions") {
      options.push({ key: "single_positions", value: "true" });
    }

    toolsWithDataType.push({
      toolId,
      toolType,
      dataType,
      options,
    });
  }

  toolsWithDataType.sort((a, b) => {
    const priorityA =
      DATA_TYPE_ORDER_PRIORITY[a.dataType as keyof typeof DATA_TYPE_ORDER_PRIORITY] || 999;
    const priorityB =
      DATA_TYPE_ORDER_PRIORITY[b.dataType as keyof typeof DATA_TYPE_ORDER_PRIORITY] || 999;
    return priorityA - priorityB;
  });

  const tools: JobTool[] = toolsWithDataType.map(({ dataType: _dataType, ...tool }) => tool);

  return { tools };
}
